/**
 * The survey a FaceWise study can ask: gender and age bracket, always at the
 * end. Each field is switched on or off per study — the wording and the
 * options are not the customer's to edit, only whether the question is asked.
 *
 * The questions themselves are deliberately NOT part of the draft. Seeding
 * them into `draft.demographics` on mount would make `draftHasContent()` true
 * before the customer has typed a single character, so every visit to
 * /studies/new would leave an empty draft study behind. Only the on/off
 * choice is draft state (`draft.metricFields`), and `draftHasContent()`
 * ignores it for the same reason.
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { EditableQuestion } from '@/components/questions/types'

export const METRIC_KEYS = ['gender', 'age'] as const
export type MetricKey = (typeof METRIC_KEYS)[number]

/** One metric field: the key the draft toggles it by, and the question the
 *  respondent is shown. */
export type MetricField = { key: MetricKey; question: EditableQuestion }

/**
 * Every metric field in the current language, in the order respondents meet
 * them. Ids are derived from the field key rather than generated, so switching
 * PL/EN re-labels the same questions instead of remounting them.
 */
export function useFixedMetric(): MetricField[] {
  const { t } = useTranslation('dashboard')
  return useMemo(
    () =>
      METRIC_KEYS.map((key) => ({
        key,
        question: {
          id: `meta-fixed-${key}`,
          kind: 'single' as const,
          text: t(`wizard.questions.metrics.presets.${key}.label`),
          required: false,
          // Same `meta:<key>` sections the Pro metryczka writes, so results
          // filtering reads a FaceWise study exactly like a Pro one.
          section: `meta:${key}`,
          options: (t(`wizard.questions.metrics.presets.${key}.options`, { returnObjects: true }) as string[]).map(
            (text, i) => ({ id: `meta-fixed-${key}-${i}`, text }),
          ),
        },
      })),
    [t],
  )
}

/** Just the questions this study asks — what the preview walks through and
 *  what the launch appends to the questionnaire. */
export function selectedMetric(metric: MetricField[], keys: MetricKey[]): EditableQuestion[] {
  return metric.filter((field) => keys.includes(field.key)).map((field) => field.question)
}
